import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Control, useWatch, useFormContext } from "react-hook-form";
import { ApplicationFormType } from "@/lib/form-schemas";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import InstitutionSelect from "@/components/forms/academic/InstitutionSelect";
import { Checkbox } from "@/components/ui/checkbox";

interface DegreeFieldsProps {
  index: number;
  control: Control<ApplicationFormType>;
  degreeId: string;
  hasOtherHighSchool: boolean;
}

const DegreeFields = ({
  index,
  control,
  degreeId,
  hasOtherHighSchool,
}: DegreeFieldsProps) => {
  const { setValue } = useFormContext();

  // const degreeLevel = watch(`degrees.${index}.degreeLevel`);
  const degreeLevel = useWatch({
    control,
    name: `degrees.${index}.degreeLevel`,
  });

  const institutionValue = useWatch({
    control,
    name: `degrees.${index}.institution`,
  });

  const institutionOtherName = useWatch({
    control,
    name: `degrees.${index}.institution_other_name`,
  });

  const isHighSchoolDegree = degreeLevel === "HighSchool";
  const institutionType = isHighSchoolDegree ? "school" : "university";

  return (
    <div className="space-y-2 font-verona">
      {isHighSchoolDegree ? (
        <p>High School</p>
      ) : (
        <FormField
          control={control}
          name={`degrees.${index}.degreeLevel`}
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel htmlFor={`degreeLevel-${index}`}>
                Degree Level *
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="flex flex-wrap gap-4"
                  id={`degreeLevel-${index}`}
                >
                  {/* <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="HighSchool"
                      id={`highSchool-${index}`}
                      disabled={
                        hasOtherHighSchool && field.value !== "HighSchool"
                      }
                    />
                    <Label htmlFor={`highSchool-${index}`}>High School</Label>
                  </div> */}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="Bachelors"
                      id={`bachelors-${index}`}
                    />
                    <Label htmlFor={`bachelors-${index}`}>Bachelors</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Masters" id={`masters-${index}`} />
                    <Label htmlFor={`masters-${index}`}>Masters</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="PhD" id={`phd-${index}`} />
                    <Label htmlFor={`phd-${index}`}>PhD</Label>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name={`degrees.${index}.institution`}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormControl>
              <InstitutionSelect
                value={field.value || ""}
                onChange={(e) => field.onChange(e.target.value)}
                type={institutionType}
                otherValue={institutionOtherName || ""}
                onOtherChange={(e) => {
                  setValue(
                    `degrees.${index}.institution_other_name`,
                    e.target.value
                  );
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Hidden field for institution_other_name to ensure it's part of the form */}
      <FormField
        control={control}
        name={`degrees.${index}.institution_other_name`}
        render={({ field }) => (
          <input type="hidden" {...field} value={field.value || ""} />
        )}
      />

      {!isHighSchoolDegree && (
        <FormField
          control={control}
          name={`degrees.${index}.degree_title`}
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel htmlFor={`degree_title-${index}`}>
                Degree Title *
              </FormLabel>
              <FormControl>
                <Input
                  id={`degree_title-${index}`}
                  placeholder="e.g., Bachelor of Science, Master of Business Administration"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {!isHighSchoolDegree && (
        <FormField
          control={control}
          name={`degrees.${index}.major`}
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel htmlFor={`major-${index}`}>Major *</FormLabel>
              <FormControl>
                <Input
                  id={`major-${index}`}
                  placeholder="e.g., Computer Science, Business Administration"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={control}
        name={`degrees.${index}.graduationYear`}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel htmlFor={`graduationYear-${index}`}>
              Graduation Year *
            </FormLabel>
            <FormControl>
              <Input
                id={`graduationYear-${index}`}
                placeholder="e.g., 2024"
                type="number"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`degrees.${index}.gpa`}
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel htmlFor={`gpa-${index}`}>
              GPA {isHighSchoolDegree ? "*" : ""}
            </FormLabel>
            <FormControl>
              <Input id={`gpa-${index}`} placeholder="e.g., 99.1%" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {isHighSchoolDegree && (
        <FormField
          control={control}
          name={`degrees.${index}.is_valedictorian`}
          render={({ field }) => (
            <FormItem className="space-x-2 flex items-center space-y-0">
              <FormControl>
                <Checkbox
                  id={`is_valedictorian-${index}`}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel htmlFor={`is_valedictorian-${index}`}>
                Were you a valedictorian?
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

export default DegreeFields;
